import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ls from 'localstorage-slim';
import { FaRegTimesCircle, FaRegCheckCircle, FaRegClock, FaPrint } from 'react-icons/fa';
import { Icon } from '@rsuite/icons';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import Loading from '../GeneralComponents/Loading';

function InvoiceDetails() {
  const subDomain = ls.get('subDomain');

  const invoiceUUID = useParams().uuid;
  const { t, i18n } = useTranslation();

  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [rin, setRin] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const handleChangeCancelReason = event => {
    setCancelReason(event.target.value);
  };

  const handleChangeRejectReason = event => {
    setRejectReason(event.target.value);
  };

  const handleCancelInvoice = () => {
    let cancelInvoiceToast = toast.loading('Please wait...');

    axios
      .put(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/cancel_document`,
        {
          status: 'cancelled',
          reason: cancelReason,
        },
        {
          params: { accessToken: ls.get('ETAToken'), invoiceUUID, taxpayer: ls.get('taxpayer_id') },
        }
      )
      .then(() => {
        toast.update(cancelInvoiceToast, {
          render: 'Invoice Cancelled Successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      })
      .catch(error => {
        toast.update(cancelInvoiceToast, {
          render: `${error.response.data}`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  const handleRejectInvoice = () => {
    let rejectInvoiceToast = toast.loading('Please wait...');

    axios
      .put(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/reject_document`,
        {
          status: 'rejected',
          reason: rejectReason,
        },
        {
          params: { accessToken: ls.get('ETAToken'), invoiceUUID, taxpayer: ls.get('taxpayer_id') },
        }
      )
      .then(() => {
        toast.update(rejectInvoiceToast, {
          render: 'Invoice Rejected Successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      })
      .catch(error => {
        toast.update(rejectInvoiceToast, {
          render: `${error.response.data}`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  const handleDownloadInvoice = () => {
    let downloadInvoiceToast = toast.loading('Please wait...');

    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/document_printout`, {
        responseType: 'arraybuffer',
        params: {
          accessToken: ls.get('ETAToken'),
          invoiceUUID,
          taxpayer: ls.get('taxpayer_id'),
        },
      })
      .then(async response => {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: `${invoiceDetails.document.internalID}-${invoiceDetails.document.issuer.name}.pdf`,
            types: [
              {
                description: 'PDF',
                accept: {
                  'application/pdf': ['.pdf'],
                },
              },
            ],
          });

          // Create a FileSystemWritableFileStream to write
          const writable = await handle.createWritable();

          // Write the contents of the file to the stream
          await writable.write(new Blob([response.data]));

          // Close the file and write the contents to disk
          await writable.close();

          toast.update(downloadInvoiceToast, {
            render: 'Invoice printout downloaded successfully',
            type: 'success',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } catch (err) {
          toast.update(downloadInvoiceToast, {
            render: `${err.name}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      })
      .catch(error => {
        toast.update(downloadInvoiceToast, {
          render: `${error}`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  useEffect(() => {
    async function fetchData() {
      const documentDetailsToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/document_details`,
          {
            params: {
              accessToken: ls.get('ETAToken'),
              invoiceUUID,
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setInvoiceDetails({
          ...response.data.data,
          document: JSON.parse(response.data.data.document),
        });
        setRin(response.data.rin);
        setIsLoading(false);

        toast.update(documentDetailsToast, {
          render: 'Document Details retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(documentDetailsToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(documentDetailsToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(documentDetailsToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, []);

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <div
        className='modal fade'
        id='staticBackdrop'
        data-bs-backdrop='static'
        data-bs-keyboard='false'
        tabIndex='-1'
        aria-labelledby='staticBackdropLabel'
        aria-hidden='true'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title' id='staticBackdropLabel'>
                {t('document.documentDetails.cancelModal.title')}
              </h5>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'></button>
            </div>
            <div className='modal-body'>
              <form dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
                <div className='mb-3'>
                  <label htmlFor='exampleInputReason' className='form-label'>
                    {t('document.documentDetails.cancelModal.reason')}
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    id='exampleInputReason'
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeCancelReason}
                  />
                </div>
              </form>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>
                {t('document.documentDetails.cancelModal.closeButton')}
              </button>
              <button type='button' className='btn btn-danger' onClick={handleCancelInvoice}>
                {t('document.documentDetails.cancelModal.cancelButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className='modal fade'
        id='rejectBackdrop'
        data-bs-backdrop='static'
        data-bs-keyboard='false'
        tabIndex='-1'
        aria-labelledby='rejectBackdropLabel'
        aria-hidden='true'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title' id='rejectBackdropLabel'>
                {t('document.documentDetails.rejectModal.title')}
              </h5>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'></button>
            </div>
            <div className='modal-body'>
              <form dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
                <div className='mb-3'>
                  <label htmlFor='rejectInputReason' className='form-label'>
                    {t('document.documentDetails.rejectModal.reason')}
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    id='rejectInputReason'
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeRejectReason}
                  />
                </div>
              </form>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>
                {t('document.documentDetails.rejectModal.closeButton')}
              </button>
              <button type='button' className='btn btn-danger' onClick={handleRejectInvoice}>
                {t('document.documentDetails.rejectModal.rejectButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='container-fluid'>
        <div className='shadow m-2 p-2'>
          <div className={`${i18n.language === 'ar' ? 'd-flex flex-row-reverse' : 'row mx-0'}`}>
            <div className={`${i18n.language === 'ar' ? 'text-end' : 'text-start'} col-8`}>
              <div className='row my-1 mx-2'>
                {i18n.language === 'en' ? (
                  <span className='fs-4'>
                    <strong>
                      {invoiceDetails.document.documentType === 'i'
                        ? t('document.documentDetails.invoice')
                        : invoiceDetails.document.documentType === 'c'
                        ? t('document.documentDetails.credit')
                        : t('document.documentDetails.debit')}
                    </strong>{' '}
                    {invoiceDetails.document.documentTypeVersion === '1.0'
                      ? t('newDocument.version.options.signature')
                      : t('newDocument.version.options.noSignature')}
                  </span>
                ) : (
                  <span className='fs-4'>
                    {invoiceDetails.document.documentTypeVersion === '1.0'
                      ? t('newDocument.version.options.signature')
                      : t('newDocument.version.options.noSignature')}{' '}
                    <strong>
                      {invoiceDetails.document.documentType === 'i'
                        ? t('document.documentDetails.invoice')
                        : invoiceDetails.document.documentType === 'c'
                        ? t('document.documentDetails.credit')
                        : t('document.documentDetails.debit')}
                    </strong>
                  </span>
                )}
              </div>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                } my-1 mx-2`}>
                <span dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  <span className='text-muted'>{t('document.documentDetails.id')}:</span>
                  {invoiceDetails.uuid} |{' '}
                  <span className='text-muted'>{t('document.documentDetails.internalId')}:</span>
                  {invoiceDetails.document.internalID}
                </span>
              </div>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                } my-1 mx-2`}>
                <span
                  className={`${
                    invoiceDetails.status === 'Invalid'
                      ? 'text-danger'
                      : invoiceDetails.status === 'Valid'
                      ? 'text-success'
                      : 'text-muted'
                  }`}>
                  {invoiceDetails.status === 'Invalid' ? (
                    <Icon as={FaRegTimesCircle} color='red' />
                  ) : invoiceDetails.status === 'Valid' ? (
                    <Icon as={FaRegCheckCircle} color='green' />
                  ) : (
                    <Icon as={FaRegClock} color='grey' />
                  )}
                  {invoiceDetails.status} |{' '}
                  <span dir={i18n.language === 'ar' ? 'rtl' : ''}>
                    {t('document.documentDetails.issuanceDate')}:{' '}
                    <span dir='ltr'>
                      {moment(invoiceDetails.dateTimeIssued).format('MM/DD/YYYY h:mm:ss A')} (Cairo)
                      ({moment(invoiceDetails.dateTimeIssued).utc().format('MM/DD/YYYY h:mm:ss A')}{' '}
                      UTC)
                    </span>
                  </span>
                </span>
              </div>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                } my-1 mx-2`}>
                <span dir='rtl'>
                  {t('document.documentDetails.submissionDate')}:{' '}
                  <span dir='ltr'>
                    {moment(invoiceDetails.dateTimeReceived).format('MM/DD/YYYY h:mm:ss A')} (Cairo)
                    ({moment(invoiceDetails.dateTimeReceived).utc().format('MM/DD/YYYY h:mm:ss A')}{' '}
                    UTC)
                  </span>
                </span>
              </div>
            </div>
            <div className={`${i18n.language === 'ar' ? 'text-end' : 'text-start'} col-4`}>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                } my-1 mx-2`}>
                <span dir='rtl'>
                  {t('document.documentDetails.submission')}: {invoiceDetails.submissionUUID}
                </span>
              </div>
            </div>
          </div>
          <div
            className={`${
              i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
            } align-items-baseline my-1 mx-2`}>
            <span
              className={`${
                i18n.language === 'ar' ? 'text-end' : 'text-start'
              } col fs-5 text-decoration-underline`}
              style={{ textUnderlineOffset: '10px' }}
              role='button'>
              {t('stepper.summary.title')}
            </span>
            {invoiceDetails.status === 'Valid' && invoiceDetails.receiverId === rin ? (
              <span
                className='col-2 text-end me-5 btn'
                data-bs-toggle='modal'
                data-bs-target='#rejectBackdrop'>
                <Icon as={FaRegTimesCircle} color='red' />{' '}
                {t('document.documentDetails.buttons.reject')}
              </span>
            ) : (
              ''
            )}
            {invoiceDetails.status === 'Valid' && invoiceDetails.issuerId === rin ? (
              <span
                className='col-2 text-center btn'
                data-bs-toggle='modal'
                data-bs-target='#staticBackdrop'>
                <Icon as={FaRegTimesCircle} color='red' />{' '}
                {t('document.documentDetails.buttons.cancel')}
              </span>
            ) : (
              ''
            )}
            <span className='col-1 text-end me-5 btn' onClick={handleDownloadInvoice}>
              <Icon as={FaPrint} color='Blue' /> {t('document.documentDetails.buttons.print')}
            </span>
          </div>
          <div
            className={`row my-1 mx-2 text-dark ${
              invoiceDetails.validationResults.status === 'Invalid'
                ? 'bg-danger bg-gradient bg-opacity-25'
                : ''
            }`}>
            {invoiceDetails.validationResults.status === 'Invalid' ? (
              <>
                <span className='col d-inline-block my-1'>
                  This document cannot be processed further. Please submit a new document with the
                  below point(s) resolved to continue:
                </span>
                {invoiceDetails.validationResults.validationSteps.map(step =>
                  step.status === 'Invalid' ? (
                    <>
                      <div className='row' style={{ fontSize: '13px' }}>
                        <span className='col-2 pe-0 me-0 text-nowrap'>{step.name}</span>
                        <ul className='col'>
                          {step.error.innerError.map(innerError => (
                            <li
                              className='text-start me-1'
                              key={
                                innerError.id
                              }>{`${innerError.errorCode} ${innerError.error}`}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    ''
                  )
                )}
              </>
            ) : (
              ''
            )}
          </div>
          <div className='row my-3 mx-2'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-7`}>
              <div
                className={`row mx-0 text-white py-2 ${
                  i18n.language === 'en' ? 'text-start' : 'text-end'
                }`}
                style={{ backgroundColor: '#04193d' }}>
                <span>{t('stepper.summary.issuerReceiver.issuer')}</span>
              </div>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-9`}>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } my-1`}>
                    <span>{t('stepper.summary.issuerReceiver.name')}</span>
                  </div>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                    } my-1`}>
                    <span className='border border-1 border-black'>
                      {invoiceDetails.document.issuer.name}
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-3`}>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } my-1`}>
                    <span className='text-nowrap'>{t('stepper.summary.issuerReceiver.rin')}</span>
                  </div>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                    } my-1`}>
                    <span className='border border-1 border-black'>
                      {invoiceDetails.document.issuer.id}
                    </span>
                  </div>
                </div>
              </div>
              <div
                className={`row mx-0 ${i18n.language === 'en' ? 'text-start' : 'text-end'} my-1`}>
                <span>{t('stepper.summary.issuerReceiver.type')}</span>
              </div>
              <div
                className={`row mx-0 ${
                  i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                } my-1`}>
                <span className='border border-1 border-black'>
                  {invoiceDetails.document.issuer.type === 'B'
                    ? 'Business'
                    : invoiceDetails.document.issuer.type === 'F'
                    ? 'Foreign'
                    : 'Personal'}
                </span>
              </div>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-9`}>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } my-1`}>
                    <span>{t('stepper.summary.issuerReceiver.branch')}</span>
                  </div>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                    } my-1`}>
                    <span className='border border-1 border-black'>
                      <div className='row'>
                        <span>{invoiceDetails.document.issuer.address.buildingNumber}</span>
                      </div>
                      <div className='row'>
                        <span>{invoiceDetails.document.issuer.address.street}</span>
                      </div>
                      <div className='row'>
                        <span>{`${invoiceDetails.document.issuer.address.regionCity}, ${invoiceDetails.document.issuer.address.governate}, ${invoiceDetails.document.issuer.address.country}`}</span>
                      </div>
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-3`}>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } my-1`}>
                    <span>{t('stepper.summary.issuerReceiver.code')}</span>
                  </div>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                    } my-1`}>
                    <span className='border border-1 border-black'>
                      {invoiceDetails.document.taxpayerActivityCode}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-5`}>
              <div
                className={`row mx-0 text-white py-2 ${
                  i18n.language === 'en' ? 'text-start' : 'text-end'
                }`}
                style={{ backgroundColor: '#04193d' }}>
                <span>{t('stepper.summary.issuerReceiver.receiver')}</span>
              </div>
              <div className='row'>
                <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-7`}>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } my-1`}>
                    <span>{t('stepper.summary.issuerReceiver.name')}</span>
                  </div>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                    } my-1`}>
                    <span className='border border-1 border-black'>
                      {invoiceDetails.document.receiver.name}
                    </span>
                  </div>
                </div>
                <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-5`}>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } my-1`}>
                    <span className='text-nowrap'>{t('stepper.summary.issuerReceiver.rin')}</span>
                  </div>
                  <div
                    className={`row mx-0 ${
                      i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                    } my-1`}>
                    <span className='border border-1 border-black'>
                      {invoiceDetails.document.receiver.id}
                    </span>
                  </div>
                </div>
              </div>
              <div
                className={`row mx-0 ${i18n.language === 'en' ? 'text-start' : 'text-end'} my-1`}>
                <span>{t('stepper.summary.issuerReceiver.type')}</span>
              </div>
              <div
                className={`row mx-0 ${
                  i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                } my-1`}>
                <span className='border border-1 border-black'>
                  {invoiceDetails.document.receiver.type === 'B'
                    ? 'Business'
                    : invoiceDetails.document.receiver.type === 'F'
                    ? 'Foreign'
                    : 'Personal'}
                </span>
              </div>
              <div
                className={`row mx-0 ${i18n.language === 'en' ? 'text-start' : 'text-end'} my-1`}>
                <span>{t('stepper.summary.issuerReceiver.branch')}</span>
              </div>
              <div
                className={`row mx-0 ${
                  i18n.language === 'en' ? 'text-start ms-1' : 'text-end me-1'
                } my-1`}>
                <span className='border border-1 border-black'>
                  <div className='row'>
                    <span>{invoiceDetails.document.receiver.address.buildingNumber}</span>
                  </div>
                  <div className='row'>
                    <span>{invoiceDetails.document.receiver.address.street}</span>
                  </div>
                  <div className='row'>
                    <span>{`${invoiceDetails.document.receiver.address.regionCity}, ${invoiceDetails.document.receiver.address.governate}, ${invoiceDetails.document.receiver.address.country}`}</span>
                  </div>
                </span>
              </div>
            </div>
          </div>
          <div className='row my-3 mx-2'>
            <div
              className={`${
                i18n.language === 'ar' ? 'text-end' : 'text-start'
              } col my-3 fs-5 text-decoration-underline`}
              style={{ textUnderlineOffset: '10px' }}
              role='button'>
              <span>{t('stepper.summary.lineItems.title')}</span>
            </div>
            <div className='row justify-content-center me-2'>
              <table
                className='table text-center ms-4'
                dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
                <thead
                  style={{
                    verticalAlign: 'middle',
                  }}>
                  <tr>
                    <th scope='col'>{t('stepper.summary.lineItems.table.codeName')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.itemCode')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.description')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.quantity')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.unitPrice')}</th>
                    <th scope='col'>{t('stepper.summary.lineItems.table.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceDetails.document.invoiceLines.map((invoiceLine, index) => (
                    <tr key={index}>
                      <td>{invoiceLine.description}</td>
                      <td>{invoiceLine.itemCode}</td>
                      <td>{invoiceLine.description}</td>
                      <td>
                        {invoiceLine.quantity}
                        {' - '}
                        {invoiceLine.unitType}
                      </td>
                      <td>{invoiceLine.unitValue.amountEGP}</td>
                      <td>{invoiceLine.salesTotal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className='row'></div>
        </div>
      </div>
    </>
  );
}

export default InvoiceDetails;
